import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Server, CheckCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const SecurityPage = () => {
  const { t } = useTranslation();

  const measures = [
    { icon: Lock,   title: t('security.m1Title'), description: t('security.m1Desc') },
    { icon: Server, title: t('security.m2Title'), description: t('security.m2Desc') },
    { icon: Eye,    title: t('security.m3Title'), description: t('security.m3Desc') },
    { icon: Shield, title: t('security.m4Title'), description: t('security.m4Desc') },
  ];

  const certifications = [
    t('security.cert1'),
    t('security.cert2'),
    t('security.cert3'),
    t('security.cert4'),
    t('security.cert5'),
  ];

  return (
    <>
      <Helmet>
        <title>{t('security.pageTitle')}</title>
        <meta name="description" content={t('security.metaDesc')} />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-4">{t('security.title')}</h1>
              <p className="text-xl text-gray-600">{t('security.subtitle')}</p>
            </motion.div>
          </div>
        </section>

        <section className="section">
          <div className="container-custom">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8 mb-16">
                {measures.map((measure, index) => {
                  const Icon = measure.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm"
                    >
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{measure.title}</h3>
                      <p className="text-gray-600">{measure.description}</p>
                    </motion.div>
                  );
                })}
              </div>

              <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">{t('security.certTitle')}</h2>
                <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {certifications.map((cert, index) => (
                    <div key={index} className="flex items-center gap-2 bg-green-50 rounded-lg p-4">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 mb-12">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-8 h-8 text-yellow-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{t('security.reportTitle')}</h3>
                    <p className="text-gray-700 mb-4">{t('security.reportDesc')}</p>
                    <Link to="/contact" className="btn btn-primary">{t('security.reportBtn')}</Link>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Link to="/privacy" className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:shadow-md transition-all text-center">
                  <h4 className="font-bold text-gray-900 mb-2">{t('security.relPrivacyTitle')}</h4>
                </Link>
                <Link to="/terms" className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:shadow-md transition-all text-center">
                  <h4 className="font-bold text-gray-900 mb-2">{t('security.relTermsTitle')}</h4>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SecurityPage;
